import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { Slider, Text } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useRef, useState } from "react";

import { VISITS_PER_WEEK_MAX } from "../constants";
import { getFilterSliderStyles } from "./sliderStyles";

const VPW_MIN = 1;
const VPW_MARKS = Array.from({ length: VISITS_PER_WEEK_MAX }, (_, index) => ({
  value: index + 1,
  label: String(index + 1),
}));

interface VisitsPerWeekFilterProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

export function VisitsPerWeekFilter(props: VisitsPerWeekFilterProps) {
  const { value, onChange } = props;

  const sliderValue = value ?? VISITS_PER_WEEK_MAX;
  const [localValue, setLocalValue] = useState(sliderValue);
  const pendingRef = useRef<number | undefined>(undefined);

  const handleDebouncedSave = useCallback(
    (newValue: number) => {
      onChange(newValue >= VISITS_PER_WEEK_MAX ? undefined : newValue);
    },
    [onChange]
  );

  useDebounce({
    value: localValue,
    debounceTimeInMs: 300,
    onUpdateValue: handleDebouncedSave,
  });

  useEffect(() => {
    const externalValue = value ?? VISITS_PER_WEEK_MAX;
    if (!isDefined(pendingRef.current) || pendingRef.current === externalValue) {
      pendingRef.current = undefined;
      setLocalValue(externalValue);
    }
  }, [value]);

  const displayLabel = localValue >= VISITS_PER_WEEK_MAX ? "Any" : `Up to ${localValue}/week`;

  return (
    <Stack spacing={5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text semibold variant="body1">
          Visits Per Week
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.subtle}`,
            borderRadius: theme.borderRadius?.xSmall,
            padding: 3,
          })}
        >
          {displayLabel}
        </Text>
      </Stack>

      <Slider
        aria-label="Visits Per Week Slider"
        value={localValue}
        min={VPW_MIN}
        max={VISITS_PER_WEEK_MAX}
        step={1}
        marks={VPW_MARKS}
        valueLabelDisplay="auto"
        sx={(theme) => getFilterSliderStyles(theme)}
        onChange={(_event, sliderNewValue) => {
          const newValue = sliderNewValue as number;
          pendingRef.current = newValue;
          setLocalValue(newValue);
        }}
      />
    </Stack>
  );
}
